import { createFeature, createReducer, on } from "@ngrx/store";
import {
    KnowledgeDocument,
    KnowledgeFaq,
    KnowledgeItem,
    KnowledgeSection,
    MalfunctionCode,
} from "../models/knowledge.models";
import { KnowledgeActions } from "./knowledge.actions";

export interface KnowledgeState {
    section: KnowledgeSection;
    query: string;
    malfunctions: MalfunctionCode[];
    documents: KnowledgeDocument[];
    faqs: KnowledgeFaq[];
    loading: boolean;
    loaded: boolean;
    error: boolean;
    mutation: "pending" | "success" | "error" | null;
}

const initialState: KnowledgeState = {
    section: "malfunctions",
    query: "",
    malfunctions: [],
    documents: [],
    faqs: [],
    loading: false,
    loaded: false,
    error: false,
    mutation: null,
};

export const knowledgeFeature = createFeature({
    name: "knowledge",
    reducer: createReducer(
        initialState,
        on(
            KnowledgeActions.pageOpened,
            (state): KnowledgeState => ({
                ...state,
                loading: true,
                error: false,
            })
        ),
        on(
            KnowledgeActions.dataLoaded,
            (state, data): KnowledgeState => ({
                ...state,
                ...data,
                loading: false,
                loaded: true,
                error: false,
            })
        ),
        on(
            KnowledgeActions.dataLoadFailed,
            (state): KnowledgeState => ({
                ...state,
                loading: false,
                loaded: true,
                error: true,
            })
        ),
        on(
            KnowledgeActions.sectionChanged,
            (state, { section }): KnowledgeState => ({
                ...state,
                section,
                query: "",
            })
        ),
        on(
            KnowledgeActions.queryChanged,
            (state, { query }): KnowledgeState => ({ ...state, query })
        ),
        on(
            KnowledgeActions.saveRequested,
            KnowledgeActions.deleteRequested,
            KnowledgeActions.statusChangeRequested,
            (state): KnowledgeState => ({ ...state, mutation: "pending" })
        ),
        on(
            KnowledgeActions.saveSucceeded,
            (state, { item }): KnowledgeState => ({
                ...replaceItem(state, item),
                mutation: "success",
            })
        ),
        on(
            KnowledgeActions.deleteSucceeded,
            (state, { item }): KnowledgeState => ({
                ...state,
                [item.kind]: state[item.kind].filter(
                    (current) => current.id !== item.id
                ),
                mutation: "success",
            })
        ),
        on(
            KnowledgeActions.statusChangeSucceeded,
            (state, { item }): KnowledgeState => ({
                ...replaceItem(state, item),
                mutation: "success",
            })
        ),
        on(
            KnowledgeActions.mutationFailed,
            (state): KnowledgeState => ({ ...state, mutation: "error" })
        ),
        on(
            KnowledgeActions.mutationStatusCleared,
            (state): KnowledgeState => ({ ...state, mutation: null })
        )
    ),
});

function replaceItem(
    state: KnowledgeState,
    item: KnowledgeItem
): KnowledgeState {
    if (item.kind === "malfunctions") {
        return {
            ...state,
            malfunctions: upsert(state.malfunctions, item),
        };
    }
    if (item.kind === "documents") {
        return { ...state, documents: upsert(state.documents, item) };
    }
    return { ...state, faqs: upsert(state.faqs, item) };
}

function upsert<T extends KnowledgeItem>(items: T[], item: T): T[] {
    const found = items.some((current) => current.id === item.id);
    const result = found
        ? items.map((current) => (current.id === item.id ? item : current))
        : [...items, item];
    return result.sort((a, b) => itemLabel(a).localeCompare(itemLabel(b)));
}

function itemLabel(item: KnowledgeItem): string {
    if (item.kind === "malfunctions") return item.code;
    if (item.kind === "documents") return item.fileDescription;
    return item.question;
}
